import { useEffect, useMemo, useState } from "react";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import PageHeader from "../../components/common/PageHeader";
import {
  useAssignItemStationMutation,
  useGetCategoriesQuery,
  useGetItemsQuery,
  useGetStationsQuery,
} from "../../store/api";

const DRAG_ITEM_MIME = "application/x-station-assignment-item-id";

const normalizeStationCode = (value) => {
  const code = String(value || "").trim().toUpperCase();
  return code || null;
};

const extractItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const getErrorMessage = (error, fallbackMessage) => {
  const candidate =
    error?.data?.detail ||
    error?.data?.message ||
    (typeof error?.error === "string" ? error.error : null) ||
    error?.message;

  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    return trimmed || fallbackMessage;
  }

  return fallbackMessage;
};

const allowDrop = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = "move";
};

const StationAssignment = () => {
  const { data: stationsData, isLoading: stationsLoading, error: stationsError } =
    useGetStationsQuery();
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetCategoriesQuery();

  const [selectedStationCode, setSelectedStationCode] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");
  const [poolSearch, setPoolSearch] = useState("");
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragSource, setDragSource] = useState("");
  const [uiNotice, setUiNotice] = useState(null);
  const [assignItemStation, { isLoading: isSavingAssignment }] =
    useAssignItemStationMutation();

  const stations = useMemo(
    () => (Array.isArray(stationsData) ? stationsData : []),
    [stationsData],
  );
  const categories = useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData],
  );
  const normalizedAssignedSearch = assignedSearch.trim();
  const normalizedPoolSearch = poolSearch.trim();

  const {
    data: assignedItemsData,
    isLoading: assignedItemsLoading,
    isFetching: assignedItemsFetching,
    error: assignedItemsError,
    refetch: refetchAssignedItems,
  } = useGetItemsQuery(
    selectedStationCode
      ? {
          effectiveStationCode: selectedStationCode,
          search: normalizedAssignedSearch || undefined,
        }
      : undefined,
    {
      skip: !selectedStationCode,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const {
    data: poolItemsData,
    isLoading: poolItemsLoading,
    error: poolItemsError,
    refetch: refetchPoolItems,
  } = useGetItemsQuery({
    hasStationCode: false,
    search: normalizedPoolSearch || undefined,
  }, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const activeStations = useMemo(
    () =>
      stations
        .filter((station) => station?.isActive !== false)
        .sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0)),
    [stations],
  );

  useEffect(() => {
    if (activeStations.length === 0) {
      setSelectedStationCode("");
      return;
    }

    const hasSelection = activeStations.some(
      (station) =>
        normalizeStationCode(station.stationCode) ===
        normalizeStationCode(selectedStationCode),
    );

    if (!hasSelection) {
      setSelectedStationCode(normalizeStationCode(activeStations[0].stationCode));
    }
  }, [activeStations, selectedStationCode]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category?.id) {
        map.set(category.id, category);
      }
    });
    return map;
  }, [categories]);

  const stationItems = useMemo(
    () =>
      [...extractItems(assignedItemsData)].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      ),
    [assignedItemsData],
  );

  const poolItems = useMemo(
    () =>
      [...extractItems(poolItemsData)].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      ),
    [poolItemsData],
  );

  const readDraggedItemId = (event) =>
    event.dataTransfer.getData(DRAG_ITEM_MIME) ||
    event.dataTransfer.getData("text/plain");

  const handleDragStart = (event, itemId, source) => {
    const safeItemId = String(itemId || "");
    event.dataTransfer.setData(DRAG_ITEM_MIME, safeItemId);
    event.dataTransfer.setData("text/plain", safeItemId);
    event.dataTransfer.effectAllowed = "move";
    setDraggedItemId(itemId);
    setDragSource(source);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragSource("");
  };

  const handleDropToSelectedStation = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const itemId = readDraggedItemId(event);
    if (!itemId) return;
    if (dragSource === "assigned") {
      handleDragEnd();
      return;
    }

    const targetStationCode = normalizeStationCode(selectedStationCode);
    if (!targetStationCode) {
      setUiNotice({
        tone: "warning",
        message: "Select a station first to assign items.",
      });
      handleDragEnd();
      return;
    }

    try {
      await assignItemStation({
        itemId,
        stationCode: targetStationCode,
      }).unwrap();
      setUiNotice(null);
    } catch (error) {
      setUiNotice({
        tone: "error",
        message: getErrorMessage(
          error,
          `Unable to assign item to ${targetStationCode}. Please try again.`,
        ),
      });
    }
    handleDragEnd();
  };

  const handleDropToPool = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const itemId = readDraggedItemId(event);
    if (!itemId) return;
    if (dragSource === "pool") {
      handleDragEnd();
      return;
    }

    try {
      await assignItemStation({
        itemId,
        stationCode: null,
      }).unwrap();
      setUiNotice(null);
    } catch (error) {
      setUiNotice({
        tone: "error",
        message: getErrorMessage(
          error,
          "Unable to move item back to pool. Please try again.",
        ),
      });
    }
    handleDragEnd();
  };

  const selectedStationLabel =
    activeStations.find(
      (station) =>
        normalizeStationCode(station.stationCode) ===
        normalizeStationCode(selectedStationCode),
    )?.displayName || selectedStationCode || "No station selected";

  const isLoading =
    stationsLoading ||
    categoriesLoading ||
    assignedItemsLoading ||
    poolItemsLoading;

  const hasLoadError = Boolean(stationsError || assignedItemsError || poolItemsError);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Station Assignment"
        subtitle="Drag and drop items between sections to manage station mapping."
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              refetchPoolItems();
              if (selectedStationCode) {
                refetchAssignedItems();
              }
            }}
          >
            Refresh
          </Button>
        }
      />

      {uiNotice && <Alert tone={uiNotice.tone}>{uiNotice.message}</Alert>}
      {hasLoadError && (
        <Alert tone="error">
          {getErrorMessage(
            stationsError || assignedItemsError || poolItemsError,
            "Unable to load station assignment data.",
          )}
        </Alert>
      )}

      {isLoading && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading stations and catalog items...
          </p>
        </Card>
      )}

      {!isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <Card className="overflow-hidden md:col-span-3 !p-0">
            <div className="flex min-h-[76px] items-center border-b border-slate-200 dark:border-slate-700 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                1. Active Stations
              </h3>
            </div>
            <div className="h-[70vh] space-y-2 overflow-y-auto p-3">
              {activeStations.length === 0 && (
                <Alert tone="warning">
                  No active stations available. Create or activate stations first.
                </Alert>
              )}

              {activeStations.map((station) => {
                const stationCode = normalizeStationCode(station.stationCode);
                const isSelected = stationCode === normalizeStationCode(selectedStationCode);
                return (
                  <button
                    key={station.stationId || stationCode}
                    type="button"
                    onClick={() => setSelectedStationCode(stationCode)}
                    className={[
                      "w-full rounded-lg border px-3 py-2 text-left transition",
                      isSelected
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600",
                    ].join(" ")}
                  >
                    <p className="text-sm font-semibold">{station.displayName}</p>
                    <p className="text-xs opacity-80">{stationCode}</p>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="overflow-hidden md:col-span-4 !p-0">
            <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 min-h-[76px] flex items-center">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  2. Assigned Items
                </h3>
                <div className="flex items-center gap-2">
                  {(assignedItemsFetching || isSavingAssignment) && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Syncing...
                    </span>
                  )}
                  <Badge tone="info">{selectedStationLabel}</Badge>
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
              <input
                type="text"
                value={assignedSearch}
                onChange={(event) => setAssignedSearch(event.target.value)}
                placeholder="Search assigned items..."
                className="h-9 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div
              className={[
                "h-[70vh] overflow-y-auto p-3 transition",
                dragSource === "pool" ? "bg-indigo-50/60 dark:bg-indigo-900/10" : "",
              ].join(" ")}
              onDragOver={allowDrop}
              onDrop={handleDropToSelectedStation}
            >
              {stationItems.length === 0 && (
                <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  No items resolved for this station. Drag items from section 3.
                </p>
              )}

              <div className="space-y-2">
                {stationItems.map((item) => {
                  const categoryName =
                    categoriesById.get(item.categoryId)?.name || "Uncategorized";
                  const isDragging = draggedItemId === item.id;

                  return (
                    <div
                      key={item.id}
                      draggable={!isSavingAssignment}
                      onDragStart={(event) => handleDragStart(event, item.id, "assigned")}
                      onDragEnd={handleDragEnd}
                      onDragOver={allowDrop}
                      onDrop={handleDropToSelectedStation}
                      className={[
                        "rounded-lg border border-slate-200 bg-white p-3 transition dark:border-slate-700 dark:bg-slate-800",
                        isDragging ? "opacity-50" : "",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {item.name}
                          </p>
                          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {categoryName}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden md:col-span-5 !p-0">
            <div className="flex min-h-[76px] items-center border-b border-slate-200 dark:border-slate-700 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                3. Item Pool
              </h3>
            </div>

            <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
              <input
                type="text"
                value={poolSearch}
                onChange={(event) => setPoolSearch(event.target.value)}
                placeholder="Search item pool..."
                className="h-9 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div
              className={[
                "h-[70vh] overflow-y-auto p-3 transition",
                dragSource === "assigned" ? "bg-amber-50/60 dark:bg-amber-900/10" : "",
              ].join(" ")}
              onDragOver={allowDrop}
              onDrop={handleDropToPool}
            >
              {poolItems.length === 0 ? (
                <div className="flex h-full">
                  <p className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    No items found in the pool.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {poolItems.map((item) => {
                    const isDragging = draggedItemId === item.id;

                    return (
                      <div
                        key={item.id}
                        draggable={!isSavingAssignment}
                        onDragStart={(event) => handleDragStart(event, item.id, "pool")}
                        onDragEnd={handleDragEnd}
                        onDragOver={allowDrop}
                        onDrop={handleDropToPool}
                        className={[
                          "rounded-lg border border-slate-200 bg-white p-3 transition dark:border-slate-700 dark:bg-slate-800",
                          isDragging ? "opacity-50" : "",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {item.name}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StationAssignment;
